import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import theme from '../../theme'
import {
  dateToISO,
  isoDateStrToDateOrNull,
  personNameFilter,
  getStringOrNullIfEmpty,
} from '../../helpers'
import { CaseTypeType, InsuranceDataType } from '../../models'
import { useStyles } from '../../pages/styled'
import { DatePicker } from '../DatePicker'
import TextField from '../TextField'
import { InfoBox } from '../InfoBox'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'

const InsuranceForm = (params: {
  insuranceFormData?: InsuranceDataType | null
  onPageContinue?: (insuranceData: InsuranceDataType) => void
  onPageBack?: () => void
  isContinueInProgress?: boolean
  errorMessage?: string
  setIfEqual?: (isChanged: boolean) => void
  viewMode: boolean
  onChangeInsuranceRequested?: () => void
  caseType: CaseTypeType | null
}) => {
  const { isContinueInProgress = false } = params
  const classes = useStyles(theme)
  const { t } = useTranslation()

  const handleFormSubmit = (values: any) => {
    const insuranceDataParams: InsuranceDataType =
      transformFormikValueToInsuranceData(values)
    if (typeof params?.onPageContinue === 'function')
      params.onPageContinue(insuranceDataParams)
  }

  const transformFormikValueToInsuranceData = (
    values: any
  ): InsuranceDataType => {
    return {
      policyNumber: values.policyNumber,
      firstName: getStringOrNullIfEmpty(values.firstName),
      lastName: getStringOrNullIfEmpty(values.lastName),
      birthDate: dateToISO(values.birthDate),
    }
  }

  const transformInputToFormikValues = (values: InsuranceDataType | null) => {
    return {
      policyNumber: values?.policyNumber ?? '',
      firstName: values?.firstName ?? '',
      lastName: values?.lastName ?? '',
      birthDate: isoDateStrToDateOrNull(values?.birthDate),
    }
  }

  const formikInitialValues = transformInputToFormikValues(
    params.insuranceFormData ?? null
  )

  const LoginSchema = () =>
    yup.object().shape({
      policyNumber: yup
        .string()
        .required(t('firstPg.policyNumber.required'))
        .min(4, t('firstPg.policyNumber.err1')),
      firstName: yup
        .string()
        .required(t('firstPg.firstName.required'))
        .min(2, t('lengthText')),
      lastName: yup
        .string()
        .required(t('firstPg.lastName.required'))
        .min(2, t('lengthText')),
      birthDate: yup
        .date()
        .nullable()
        .min(new Date('1899-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1'))
        .required(t('firstPg.birthDate.required')),
    })

  const PolicySchema = () =>
    yup.object().shape({
      policyNumber: yup
        .string()
        .required(t('firstPg.policyNumber.required'))
        .min(4, t('firstPg.policyNumber.err1')),
    })
  const formikRef: React.Ref<FormikProps<any>> = useRef(null)

  return (
    <Ga4Form formName={'InsuranceForm'} >
      <Formik
        initialValues={formikInitialValues}
        validationSchema={params.caseType !== 'TI' ? LoginSchema : PolicySchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          values,
          errors,
          setFieldValue,
          handleChange,
          touched,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form className={classes.rootInsurance}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainFormInsurance}
              >
                <InfoBox caseType={params.caseType} />
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkAreaInsurance}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Grid
                      item
                      sm={12}
                      xl={12}
                      xs={12}
                      className={classes.centerRow}
                    >
                      <Grid item xl={12} xs={12} sm={12}>
                        <Typography className={classes.text} variant="h3">
                          {t([`firstPg.title.${params.caseType}`, `firstPg.title.default`])}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xl={6}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        {params.viewMode && (
                          <Button
                            type="button"
                            className={classes.changeButton}
                            onClick={() => {
                              if (
                                typeof params.onChangeInsuranceRequested ===
                                'function'
                              ) {
                                params?.onChangeInsuranceRequested()
                              }
                            }}
                          >
                            {t('firstPg.change')}
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={6} xs={6} sm={6}>
                        <TextField
                          variant={'outlined'}
                          // @ts-ignore
                          label={
                            params.caseType !== 'TI'
                              ? t('firstPg.policyNumber.label')
                              : t('firstPg.policyNumber.labelTI')
                          }
                          name="policyNumber"
                          value={values.policyNumber}
                          error={!!(errors.policyNumber && touched.policyNumber)}
                          placeholder={t('firstPg.policyNumber.hint')}
                          helperText={
                            errors.policyNumber && touched.policyNumber
                              ? errors.policyNumber
                              : null
                          }
                          onChange={handleChange}
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{ maxLength: 20 }}
                          onBlur={handleBlur}
                          required={!params.viewMode}
                          disabled={params.viewMode}
                        />
                      </Grid>
                    </Grid>
                    {params.caseType !== 'TI' && (
                      <React.Fragment>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid item xl={6} xs={6} sm={6}>
                            <TextField
                              regex={personNameFilter}
                              // @ts-ignore
                              label={t('firstPg.firstName.label')}
                              placeholder={t('firstPg.firstName.hint')}
                              error={!!(errors.firstName && touched.firstName)}
                              name="firstName"
                              onChange={handleChange}
                              value={values.firstName}
                              helperText={
                                errors.firstName && touched.firstName
                                  ? errors.firstName
                                  : null
                              }
                              className={classes.fieldSize}
                              InputProps={{ required: false }}
                              inputProps={{
                                maxLength: 59,
                              }}
                              onBlur={handleBlur}
                              required={!params.viewMode}
                              disabled={params.viewMode}
                            />
                          </Grid>
                          <Grid item xl={6} xs={6} sm={6}>
                            <TextField
                              regex={personNameFilter}
                              // @ts-ignore
                              label={t('firstPg.lastName.label')}
                              placeholder={t('firstPg.lastName.hint')}
                              error={!!(errors.lastName && touched.lastName)}
                              name="lastName"
                              value={values.lastName}
                              onChange={handleChange}
                              helperText={
                                errors.lastName && touched.lastName
                                  ? errors.lastName
                                  : null
                              }
                              className={classes.fieldSize}
                              InputProps={{ required: false }}
                              inputProps={{
                                maxLength: 60,
                              }}
                              onBlur={handleBlur}
                              required={!params.viewMode}
                              disabled={params.viewMode}
                            />
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          xl={12}
                          container
                          className={classes.gridItem}
                          spacing={2}
                        >
                          <Grid item xl={6} xs={6} sm={6}>
                            <DatePicker
                              name="birthDate"
                              label={t('firstPg.birthDate.label')}
                              error={Boolean(
                                errors.birthDate && touched.birthDate
                              )}
                              helperText={
                                errors.birthDate && touched.birthDate
                                  ? errors.birthDate
                                  : null
                              }
                              required={true}
                              maxDate={new Date()}
                              minDate={new Date(1900, 0, 1)}
                              value={values.birthDate}
                              onChange={(date) =>
                                setFieldValue('birthDate', date)
                              }
                              //@ts-ignore
                              placeholder={t('firstPg.birthDate.hint')}
                              disabled={params.viewMode}
                            />
                          </Grid>
                        </Grid>
                      </React.Fragment>
                    )}

                    {!params.viewMode && (
                      <ErrorSubmitBack
                        name={'InsuranceForm'}
                        formik={formikRef.current}
                        pending={
                          isContinueInProgress /*!isAuth ? pending : pendingInsurance*/
                        }
                        backLinkText={t('firstPg.backBtn')}
                        onBackLinkClick={params.onPageBack}
                      >
                        {t('continueBtn')}
                      </ErrorSubmitBack>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form>
  )
}

// @ts-ignore
export default InsuranceForm
